import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

import { supabase } from '../../../lib/supabase';

const LEAD_SOURCES = ['Website', 'Referral', 'Google', 'Facebook', 'Thumbtack', 'Yelp', 'HomeAdvisor', 'Angi', 'Nextdoor', 'Word of Mouth', 'Other'];
const PAYMENT_METHODS = ['Cash', 'Check', 'Credit Card', 'Debit Card', 'Zelle', 'Venmo', 'PayPal', 'ACH', 'Other'];
const CLIENT_STATUSES = ['Lead', 'Active', 'Returning', 'VIP', 'Inactive', 'Blocked'];

const DEFAULT_STATUS_META = {
  Lead:      { color: 'bg-blue-500/10 text-blue-600 border-blue-200', dot: 'bg-blue-500' },
  Active:    { color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200', dot: 'bg-emerald-500' },
  Returning: { color: 'bg-violet-500/10 text-violet-600 border-violet-200', dot: 'bg-violet-500' },
  VIP:       { color: 'bg-amber-500/10 text-amber-600 border-amber-200', dot: 'bg-amber-500' },
  Inactive:  { color: 'bg-slate-400/10 text-slate-500 border-slate-200', dot: 'bg-slate-400' },
  Blocked:   { color: 'bg-red-500/10 text-red-600 border-red-200', dot: 'bg-red-500' },
};

const StatusBadge = ({ status, statusMeta = DEFAULT_STATUS_META }) => {
  const meta = statusMeta?.[status] || { color: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta?.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot}`} />
      {status}
    </span>
  );
};

const SectionHeader = ({ title }) => (
  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{title}</h4>
);

const MapPreview = ({ address }) => {
  const [viewMode, setViewMode] = useState('street');
  const [streetViewError, setStreetViewError] = useState(false);

  if (!address) return null;

  const encoded = encodeURIComponent(address);
  const MAPS_KEY = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY || '';
  const streetViewImgUrl = `https://maps.googleapis.com/maps/api/streetview?size=640x300&location=${encoded}&fov=90&heading=235&pitch=10&key=${MAPS_KEY}`;
  const mapUrl = `https://maps.google.com/maps?q=${encoded}&t=k&z=17&output=embed`;

  return (
    <div className="relative w-full h-48 bg-muted overflow-hidden border-b border-border">
      <div className="absolute top-2 left-2 z-10 flex gap-1">
        <button
          onClick={() => setViewMode('street')}
          className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md shadow-sm transition-colors ${viewMode === 'street' ? 'bg-primary text-primary-foreground' : 'bg-white/90 text-gray-700 hover:bg-white'}`}
        >
          <Icon name="Home" size={10} />
          Street View
        </button>
        <button
          onClick={() => setViewMode('map')}
          className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md shadow-sm transition-colors ${viewMode === 'map' ? 'bg-primary text-primary-foreground' : 'bg-white/90 text-gray-700 hover:bg-white'}`}
        >
          <Icon name="Map" size={10} />
          Satellite
        </button>
      </div>

      {viewMode === 'street' ? (
        streetViewError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-muted">
            <Icon name="MapPin" size={28} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Street View not available for this address</p>
          </div>
        ) : (
          <img
            key={`street-${address}`}
            src={streetViewImgUrl}
            alt={`Street view of ${address}`}
            className="w-full h-full object-cover"
            referrerPolicy="origin"
            onError={() => setStreetViewError(true)}
          />
        )
      ) : (
        <iframe
          key={`map-${address}`}
          title="Satellite map of client address"
          src={mapUrl}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      )}

      <div className="absolute bottom-2 right-2 z-10">
        <a
          href={`https://maps.google.com/?q=${encoded}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 bg-white/90 backdrop-blur-sm text-xs text-gray-700 px-2 py-1 rounded-md shadow-sm hover:bg-white transition-colors"
        >
          <Icon name="ExternalLink" size={11} />
          Open in Maps
        </a>
      </div>
    </div>
  );
};

const JobHistoryItem = ({ job }) => {
  const statusColors = {
    completed: 'bg-emerald-100 text-emerald-700',
    in_progress: 'bg-blue-100 text-blue-700',
    scheduled: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-600',
  };
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 transition-colors">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name="Briefcase" size={14} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-foreground leading-tight truncate">{job?.title}</p>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusColors?.[job?.status] || 'bg-gray-100 text-gray-600'}`}>
            {job?.status?.replace('_', ' ')}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{job?.service_type}</p>
        <div className="flex items-center gap-3 mt-1">
          {job?.scheduled_date && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Icon name="Calendar" size={10} />
              {new Date(job?.scheduled_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
          {(job?.total_cost > 0 || job?.labor_cost > 0) && (
            <span className="text-xs font-semibold text-foreground">
              ${Number(job?.total_cost || job?.labor_cost || 0)?.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline editable field component
const EditableField = ({ label, icon, value, onSave, type = 'text', placeholder = '' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (editing) inputRef?.current?.focus();
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleSave();
    if (e?.key === 'Escape') { setDraft(value || ''); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <Icon name={icon} size={15} className="text-muted-foreground flex-shrink-0" />
        <div className="flex-1 flex items-center gap-1.5">
          <input
            ref={inputRef}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e?.target?.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 text-sm border border-primary/50 rounded-md px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button onClick={handleSave} className="p-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
            <Icon name="Check" size={12} />
          </button>
          <button onClick={() => { setDraft(value || ''); setEditing(false); }} className="p-1 rounded-md bg-muted hover:bg-muted/80 transition-colors">
            <Icon name="X" size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-1.5 group">
      <Icon name={icon} size={15} className="text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {label && <p className="text-[10px] text-muted-foreground uppercase tracking-wide leading-none mb-0.5">{label}</p>}
        <p className="text-sm text-foreground leading-snug">{value || <span className="text-muted-foreground italic">Not set</span>}</p>
      </div>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-muted flex-shrink-0"
      >
        <Icon name="Pencil" size={12} className="text-muted-foreground" />
      </button>
    </div>
  );
};

const ContactDrawer = ({ contact, isOpen, onClose, onDelete, onUpdate, statusMeta = DEFAULT_STATUS_META, clientStatuses = CLIENT_STATUSES }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [notes, setNotes] = useState('');
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [leadSource, setLeadSource] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [editingLeadSource, setEditingLeadSource] = useState(false);
  const [editingPayment, setEditingPayment] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [clientStatus, setClientStatus] = useState('Lead');
  const [saving, setSaving] = useState(false);
  const [primaryAddress, setPrimaryAddress] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  // Local contact fields state for inline editing
  const [localContact, setLocalContact] = useState({});

  useEffect(() => {
    if (contact && isOpen) {
      setNotes(contact?.notes || '');
      setLeadSource(contact?.lead_source || contact?.source || '');
      setPaymentMethod(contact?.payment_method || '');
      setClientStatus(contact?.client_status || 'Lead');
      const parts = [contact?.address, contact?.city, contact?.state, contact?.zip]?.filter(Boolean);
      setPrimaryAddress(parts?.join(', ') || '');
      setActiveTab('overview');
      setLocalContact({
        first_name: contact?.first_name || '',
        last_name: contact?.last_name || '',
        name: contact?.name || '',
        phone: contact?.phone || '',
        email: contact?.email || '',
        company: contact?.company || '',
        title: contact?.title || '',
        address: contact?.address || '',
        city: contact?.city || '',
        state: contact?.state || '',
        zip: contact?.zip || '',
      });
      fetchJobs();
      fetchAttachments();
    }
  }, [contact?.id, isOpen]);

  useEffect(() => {
    if (activeTab === 'calls' && contact?.phone) {
      fetchCallHistory();
    }
  }, [activeTab, contact?.phone]);

  const fetchJobs = async () => {
    if (!contact?.id) return;
    setJobsLoading(true);
    const { data } = await supabase
      ?.from('jobs')
      ?.select('id, title, status, service_type, scheduled_date, total_cost, labor_cost, address, location')
      ?.eq('client_id', contact?.id)
      ?.is('deleted_at', null)
      ?.order('created_at', { ascending: false })
      ?.limit(20);
    const jobList = data || [];
    setJobs(jobList);
    if (!primaryAddress && jobList?.length > 0) {
      const addr = jobList?.[0]?.address || jobList?.[0]?.location;
      if (addr) setPrimaryAddress(addr);
    }
    setJobsLoading(false);
  };

  const fetchCallHistory = async () => {
    if (!contact?.phone) return;
    setCallsLoading(true);
    const { data } = await supabase
      ?.from('calls')
      ?.select('*')
      ?.eq('caller_phone', contact?.phone)
      ?.is('deleted_at', null)
      ?.order('created_at', { ascending: false });
    setCallHistory(data || []);
    setCallsLoading(false);
  };

  const fetchAttachments = async () => {
    if (!contact?.id) return;
    setAttachmentsLoading(true);
    try {
      const { data, error } = await supabase?.storage?.from('contact-attachments')?.list(`contacts/${contact?.id}`, { sortBy: { column: 'created_at', order: 'desc' } });
      if (!error && data) {
        setAttachments(data?.filter(f => f?.name !== '.emptyFolderPlaceholder'));
      }
    } catch (e) {
      // bucket may not exist yet
    }
    setAttachmentsLoading(false);
  };

  const saveField = async (field, value) => {
    if (!contact?.id) return;
    setSaving(true);
    await supabase?.from('contacts')?.update({ [field]: value, updated_at: new Date()?.toISOString() })?.eq('id', contact?.id);
    setSaving(false);
    if (onUpdate) onUpdate();
  };

  const handleSaveContactField = async (field, value) => {
    setLocalContact(prev => ({ ...prev, [field]: value }));
    await saveField(field, value);
    // Update address display if address fields changed
    if (['address', 'city', 'state', 'zip']?.includes(field)) {
      const updated = { ...localContact, [field]: value };
      const parts = [updated?.address, updated?.city, updated?.state, updated?.zip]?.filter(Boolean);
      setPrimaryAddress(parts?.join(', ') || '');
    }
  };

  const handleSaveNote = async () => {
    if (!newNote?.trim()) return;
    setSavingNote(true);
    const combined = notes ? `${notes}\n\n[${new Date()?.toLocaleDateString()}] ${newNote?.trim()}` : `[${new Date()?.toLocaleDateString()}] ${newNote?.trim()}`;
    const { error } = await supabase?.from('contacts')?.update({ notes: combined, updated_at: new Date()?.toISOString() })?.eq('id', contact?.id);
    if (!error) {
      setNotes(combined);
      setNewNote('');
      if (onUpdate) onUpdate();
    }
    setSavingNote(false);
  };

  const handleSaveLeadSource = async (val) => {
    setLeadSource(val);
    setEditingLeadSource(false);
    await saveField('lead_source', val);
  };

  const handleSavePayment = async (val) => {
    setPaymentMethod(val);
    setEditingPayment(false);
    await saveField('payment_method', val);
  };

  const handleSaveStatus = async (val) => {
    setClientStatus(val);
    setEditingStatus(false);
    await saveField('client_status', val);
    if (onUpdate) onUpdate();
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length || !contact?.id) return;
    setUploadingFile(true);
    for (const file of files) {
      const ext = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;
      const filePath = `contacts/${contact?.id}/${fileName}`;
      const { error } = await supabase?.storage?.from('contact-attachments')?.upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (!error) {
        // store original name as metadata by renaming — use metadata approach
      }
    }
    await fetchAttachments();
    setUploadingFile(false);
    // reset input
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDeleteAttachment = async (fileName) => {
    const filePath = `contacts/${contact?.id}/${fileName}`;
    await supabase?.storage?.from('contact-attachments')?.remove([filePath]);
    setAttachments(prev => prev?.filter(f => f?.name !== fileName));
  };

  const getAttachmentUrl = async (fileName) => {
    const filePath = `contacts/${contact?.id}/${fileName}`;
    const { data } = await supabase?.storage?.from('contact-attachments')?.createSignedUrl(filePath, 3600);
    return data?.signedUrl;
  };

  const handleDownloadAttachment = async (fileName) => {
    const url = await getAttachmentUrl(fileName);
    if (url) window.open(url, '_blank');
  };

  const formatDuration = (secs) => {
    if (!secs) return '0:00';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const getFileIcon = (name) => {
    const ext = name?.split('.')?.pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp']?.includes(ext)) return 'Image';
    if (ext === 'pdf') return 'FileText';
    if (['doc', 'docx']?.includes(ext)) return 'FileText';
    return 'File';
  };

  const totalJobValue = jobs?.reduce((sum, j) => sum + Number(j?.total_cost || j?.labor_cost || 0), 0);
  const completedJobs = jobs?.filter(j => j?.status === 'completed')?.length;

  if (!contact) return null;

  const displayName = localContact?.name || `${localContact?.first_name || ''} ${localContact?.last_name || ''}`?.trim() || contact?.name || '';
  const displayAddress = primaryAddress || contact?.address || '';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'history', label: 'History', icon: 'Briefcase', count: jobs?.length },
    { id: 'calls', label: 'Calls', icon: 'Phone', count: callHistory?.length || null },
    { id: 'notes', label: 'Notes', icon: 'FileText' },
    { id: 'attachments', label: 'Files', icon: 'Paperclip', count: attachments?.length || null },
  ];

  const selectedCall = callHistory?.find((c) => c?.id === selectedCallId);

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[420px] lg:w-[480px] bg-background border-l border-border z-50 transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <Icon name="ChevronLeft" size={18} className="text-muted-foreground" />
            </button>
            <span className="text-sm font-semibold text-foreground">Contact</span>
          </div>
          <div className="flex items-center gap-1">
            {saving && <span className="text-xs text-muted-foreground">Saving...</span>}
            {onDelete && (
              <button
                onClick={() => { onDelete(contact?.id); onClose(); }}
                className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              >
                <Icon name="Trash2" size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* Map Preview */}
          <MapPreview address={displayAddress} />

          {/* Contact Hero */}
          <div className="px-5 pt-4 pb-3 border-b border-border">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-primary/20 to-primary/10 flex-shrink-0 flex items-center justify-center border-2 border-background shadow-sm">
                {contact?.avatar ? (
                  <Image src={contact?.avatar} alt={displayName || 'Contact avatar'} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-primary">
                    {(displayName || '?')?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-foreground leading-tight">{displayName}</h2>
                {localContact?.title && <p className="text-sm text-muted-foreground">{localContact?.title}</p>}
                {localContact?.company && <p className="text-xs text-muted-foreground">{localContact?.company}</p>}
                <div className="flex items-center gap-2 mt-2">
                  {editingStatus ? (
                    <div className="flex flex-wrap gap-1.5">
                      {clientStatuses?.map(s => {
                        const meta = statusMeta?.[s] || DEFAULT_STATUS_META?.[s];
                        return (
                          <button
                            key={s}
                            onClick={() => handleSaveStatus(s)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border transition-all ${clientStatus === s ? (meta?.color || '') + ' ring-2 ring-offset-1 ring-primary/20' : 'bg-muted/50 text-muted-foreground border-border hover:border-foreground/30'}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot || 'bg-muted-foreground'}`} />
                            {s}
                          </button>
                        );
                      })}
                      <button onClick={() => setEditingStatus(false)} className="text-[10px] text-muted-foreground hover:text-foreground px-1">✕</button>
                    </div>
                  ) : (
                    <button onClick={() => setEditingStatus(true)} className="group flex items-center gap-1.5">
                      <StatusBadge status={clientStatus} statusMeta={statusMeta} />
                      <Icon name="Pencil" size={11} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                  {completedJobs > 0 && (
                    <span className="text-xs text-muted-foreground">{completedJobs} job{completedJobs !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2 mt-4">
              {[
                { icon: 'Phone', label: 'Call', color: 'text-emerald-600', bg: 'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30', action: () => localContact?.phone && window.open(`tel:${localContact?.phone}`) },
                { icon: 'MessageSquare', label: 'SMS', color: 'text-blue-600', bg: 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30', action: () => localContact?.phone && window.open(`sms:${localContact?.phone}`) },
                { icon: 'Mail', label: 'Email', color: 'text-violet-600', bg: 'bg-violet-50 hover:bg-violet-100 dark:bg-violet-900/20 dark:hover:bg-violet-900/30', action: () => localContact?.email && window.open(`mailto:${localContact?.email}`) },
                { icon: 'MapPin', label: 'Map', color: 'text-rose-600', bg: 'bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30', action: () => displayAddress && window.open(`https://maps.google.com/?q=${encodeURIComponent(displayAddress)}`, '_blank') },
              ]?.map((btn) => (
                <button
                  key={btn?.label}
                  onClick={btn?.action}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl ${btn?.bg} transition-colors`}
                >
                  <Icon name={btn?.icon} size={18} className={btn?.color} />
                  <span className={`text-[10px] font-medium ${btn?.color}`}>{btn?.label}</span>
                </button>
              ))}
            </div>

            {/* Stats Row */}
            {jobs?.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-base font-bold text-foreground">{jobs?.length}</p>
                  <p className="text-[10px] text-muted-foreground">Total Jobs</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-base font-bold text-emerald-600">{completedJobs}</p>
                  <p className="text-[10px] text-muted-foreground">Completed</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-base font-bold text-foreground">${totalJobValue?.toFixed(0)}</p>
                  <p className="text-[10px] text-muted-foreground">Total Value</p>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-border flex-shrink-0 bg-background sticky top-0 z-10">
            <nav className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={13} />
                  {tab?.label}
                  {tab?.count > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === tab?.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="pb-6">

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="divide-y divide-border">

                {/* Contact Info — all fields editable */}
                <div className="px-5 py-4">
                  <SectionHeader title="Contact Info" />
                  <p className="text-[10px] text-muted-foreground mb-2 flex items-center gap-1">
                    <Icon name="Pencil" size={10} />
                    Hover over a field to edit
                  </p>
                  <div className="space-y-0.5">
                    <EditableField
                      icon="User"
                      label="First Name"
                      value={localContact?.first_name}
                      onSave={(v) => handleSaveContactField('first_name', v)}
                      placeholder="First name"
                    />
                    <EditableField
                      icon="User"
                      label="Last Name"
                      value={localContact?.last_name}
                      onSave={(v) => handleSaveContactField('last_name', v)}
                      placeholder="Last name"
                    />
                    <EditableField
                      icon="Phone"
                      label="Phone"
                      value={localContact?.phone}
                      onSave={(v) => handleSaveContactField('phone', v)}
                      type="tel"
                      placeholder="Phone number"
                    />
                    <EditableField
                      icon="Mail"
                      label="Email"
                      value={localContact?.email}
                      onSave={(v) => handleSaveContactField('email', v)}
                      type="email"
                      placeholder="Email address"
                    />
                    <EditableField
                      icon="Building2"
                      label="Company"
                      value={localContact?.company}
                      onSave={(v) => handleSaveContactField('company', v)}
                      placeholder="Company name"
                    />
                    <EditableField
                      icon="Briefcase"
                      label="Title"
                      value={localContact?.title}
                      onSave={(v) => handleSaveContactField('title', v)}
                      placeholder="Job title"
                    />
                  </div>
                </div>

                {/* Address — editable */}
                <div className="px-5 py-4">
                  <SectionHeader title="Address" />
                  <div className="space-y-0.5">
                    <EditableField
                      icon="MapPin"
                      label="Street"
                      value={localContact?.address}
                      onSave={(v) => handleSaveContactField('address', v)}
                      placeholder="Street address"
                    />
                    <EditableField
                      icon="MapPin"
                      label="City"
                      value={localContact?.city}
                      onSave={(v) => handleSaveContactField('city', v)}
                      placeholder="City"
                    />
                    <EditableField
                      icon="MapPin"
                      label="State"
                      value={localContact?.state}
                      onSave={(v) => handleSaveContactField('state', v)}
                      placeholder="State"
                    />
                    <EditableField
                      icon="MapPin"
                      label="ZIP"
                      value={localContact?.zip}
                      onSave={(v) => handleSaveContactField('zip', v)}
                      placeholder="ZIP code"
                    />
                  </div>
                  {displayAddress && (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(displayAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                    >
                      <Icon name="Navigation" size={11} />
                      View on Google Maps
                    </a>
                  )}
                </div>

                {/* Client Status */}
                <div className="px-5 py-4">
                  <SectionHeader title="Client Status" />
                  <div className="flex flex-wrap gap-1.5">
                    {clientStatuses?.map(s => {
                      const meta = statusMeta?.[s] || DEFAULT_STATUS_META?.[s];
                      return (
                        <button
                          key={s}
                          onClick={() => handleSaveStatus(s)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${clientStatus === s ? (meta?.color || '') + ' ring-2 ring-offset-1 ring-primary/20' : 'bg-muted/50 text-muted-foreground border-border hover:border-foreground/30'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot || 'bg-muted-foreground'}`} />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Method */}
                <div className="px-5 py-4">
                  <SectionHeader title="Payment Method" />
                  {editingPayment ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {PAYMENT_METHODS?.map((pm) => (
                          <button
                            key={pm}
                            onClick={() => handleSavePayment(pm)}
                            className={`text-xs py-2 px-3 rounded-lg border transition-colors text-left ${paymentMethod === pm ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border hover:border-primary/50 text-foreground'}`}
                          >
                            {pm}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setEditingPayment(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="CreditCard" size={15} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{paymentMethod || <span className="text-muted-foreground italic">Not set</span>}</span>
                      </div>
                      <button onClick={() => setEditingPayment(true)} className="text-xs text-primary hover:underline flex items-center gap-1">
                        <Icon name="Pencil" size={11} />
                        {paymentMethod ? 'Change' : 'Add'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Lead Source */}
                <div className="px-5 py-4">
                  <SectionHeader title="Lead Source" />
                  {editingLeadSource ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {LEAD_SOURCES?.map((src) => (
                          <button
                            key={src}
                            onClick={() => handleSaveLeadSource(src)}
                            className={`text-xs py-2 px-3 rounded-lg border transition-colors text-left ${leadSource === src ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border hover:border-primary/50 text-foreground'}`}
                          >
                            {src}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setEditingLeadSource(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="Megaphone" size={15} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{leadSource || <span className="text-muted-foreground italic">Not set</span>}</span>
                      </div>
                      <button onClick={() => setEditingLeadSource(true)} className="text-xs text-primary hover:underline flex items-center gap-1">
                        <Icon name="Pencil" size={11} />
                        {leadSource ? 'Change' : 'Add'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="px-5 py-4">
                  <SectionHeader title="Tags" />
                  <div className="flex flex-wrap gap-1.5">
                    <StatusBadge status={clientStatus} statusMeta={statusMeta} />
                    {localContact?.company && (
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full border border-primary/20">{localContact?.company}</span>
                    )}
                    {leadSource && (
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full border border-violet-200">{leadSource}</span>
                    )}
                    {jobs?.length > 0 && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full border border-emerald-200">
                        {jobs?.length} Job{jobs?.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="px-5 py-4">
                <div className="flex items-center justify-between mb-4">
                  <SectionHeader title={`Job History (${jobs?.length})`} />
                </div>
                {jobsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3]?.map((i) => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}
                  </div>
                ) : jobs?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mb-3">
                      <Icon name="Briefcase" size={20} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">No job history</p>
                    <p className="text-xs text-muted-foreground">Jobs linked to this contact will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {jobs?.map((job) => <JobHistoryItem key={job?.id} job={job} />)}
                  </div>
                )}
              </div>
            )}

            {/* CALLS TAB */}
            {activeTab === 'calls' && (
              <div className="flex flex-col">
                {callsLoading ? (
                  <div className="p-5 space-y-3">
                    {[1, 2, 3]?.map((i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
                  </div>
                ) : callHistory?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-3">
                      <Icon name="Phone" size={20} className="text-violet-500" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">No call history</p>
                    <p className="text-xs text-muted-foreground">Calls from this contact's phone number will appear here.</p>
                  </div>
                ) : (
                  <div>
                    <div className="border-b border-border max-h-56 overflow-y-auto">
                      {callHistory?.map((call) => (
                        <button
                          key={call?.id}
                          onClick={() => setSelectedCallId(selectedCallId === call?.id ? null : call?.id)}
                          className={`w-full text-left p-4 border-b border-border transition-colors hover:bg-muted/50 ${selectedCallId === call?.id ? 'bg-violet-50 border-l-2 border-l-violet-600' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${call?.status === 'missed' ? 'bg-red-100' : 'bg-green-100'}`}>
                              <Icon name={call?.status === 'missed' ? 'PhoneMissed' : 'PhoneIncoming'} size={14} className={call?.status === 'missed' ? 'text-red-500' : 'text-green-600'} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-foreground capitalize">{call?.status}</p>
                                <span className="text-xs text-muted-foreground">{formatDate(call?.created_at)}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{formatDuration(call?.duration_seconds)} • {call?.outcome || 'other'}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    {selectedCall ? (
                      <div className="p-5 space-y-4">
                        {selectedCall?.ai_summary && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                              <Icon name="Sparkles" size={14} className="text-violet-500" />
                              Summary
                            </h4>
                            <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg">
                              <p className="text-sm text-foreground leading-relaxed">{selectedCall?.ai_summary}</p>
                            </div>
                          </div>
                        )}
                        {selectedCall?.transcript && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                              <Icon name="FileText" size={14} className="text-muted-foreground" />
                              Transcript
                            </h4>
                            <div className="p-3 bg-muted rounded-lg max-h-48 overflow-y-auto">
                              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{selectedCall?.transcript}</p>
                            </div>
                          </div>
                        )}
                        {!selectedCall?.ai_summary && !selectedCall?.transcript && (
                          <p className="text-sm text-muted-foreground text-center py-4">No transcript available.</p>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">Select a call above to view transcript</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NOTES TAB */}
            {activeTab === 'notes' && (
              <div className="px-5 py-4">
                <SectionHeader title="Notes" />
                <div className="space-y-3 mb-4">
                  <textarea
                    placeholder="Add a note about this contact..."
                    value={newNote}
                    onChange={(e) => setNewNote(e?.target?.value)}
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  />
                  <Button
                    onClick={handleSaveNote}
                    disabled={!newNote?.trim() || savingNote}
                    size="sm"
                    className="w-full"
                  >
                    <Icon name="Plus" size={14} className="mr-1.5" />
                    {savingNote ? 'Saving...' : 'Add Note'}
                  </Button>
                </div>
                {notes ? (
                  <div className="space-y-2">
                    {notes?.split('\n\n')?.filter(Boolean)?.reverse()?.map((note, i) => (
                      <div key={i} className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{note}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Icon name="StickyNote" size={24} className="text-muted-foreground mb-2" />
                    <p className="text-xs text-muted-foreground">No notes yet. Add your first note above.</p>
                  </div>
                )}
              </div>
            )}

            {/* ATTACHMENTS TAB */}
            {activeTab === 'attachments' && (
              <div className="px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <SectionHeader title="Attachments" />
                  <button
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={uploadingFile}
                    className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
                  >
                    <Icon name="Plus" size={11} />
                    {uploadingFile ? 'Uploading...' : 'Add File'}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                />

                {attachmentsLoading ? (
                  <div className="space-y-2">
                    {[1, 2]?.map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />)}
                  </div>
                ) : attachments?.length === 0 ? (
                  <button
                    onClick={() => fileInputRef?.current?.click()}
                    className="w-full border-2 border-dashed border-border rounded-lg py-6 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-muted/30 transition-colors"
                  >
                    <Icon name="Paperclip" size={20} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Tap to add files, photos, or documents</span>
                    <span className="text-[10px] text-muted-foreground">Images, PDF, Word — max 10MB</span>
                  </button>
                ) : (
                  <div className="space-y-2">
                    {attachments?.map((file) => (
                      <div key={file?.name} className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors group">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={getFileIcon(file?.name)} size={14} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{file?.name}</p>
                          {file?.metadata?.size && (
                            <p className="text-[10px] text-muted-foreground">{formatFileSize(file?.metadata?.size)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleDownloadAttachment(file?.name)}
                            className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            title="Download"
                          >
                            <Icon name="Download" size={13} className="text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleDeleteAttachment(file?.name)}
                            className="p-1.5 rounded-md hover:bg-destructive/10 transition-colors"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={13} className="text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Upload more button */}
                    <button
                      onClick={() => fileInputRef?.current?.click()}
                      disabled={uploadingFile}
                      className="w-full border border-dashed border-border rounded-lg py-2.5 flex items-center justify-center gap-2 hover:border-primary/40 hover:bg-muted/20 transition-colors text-xs text-muted-foreground disabled:opacity-50"
                    >
                      <Icon name="Plus" size={13} />
                      {uploadingFile ? 'Uploading...' : 'Add more files'}
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default ContactDrawer;